import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Users, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/Field";
import { api, fmtINR } from "@/lib/api";
import { errText } from "@/lib/aadhaar";
import { useLang } from "@/context/LanguageContext";
import { TAGLINE, KITS_NOTE, OFFER_END_LABEL } from "@/lib/offer";

const FEES = { municipal: { yearly: 150000, monthly: 20000 }, rural: { yearly: 100000, monthly: 12000 } };
const empty = { school_name: "", contact_person: "", designation: "", email: "", phone: "", city: "", state: "", area: "municipal", billing: "yearly", message: "" };

// CSV columns: student_name, class_grade, parent_phone (header row optional)
const parseRoster = (text) => text.split(/\r?\n/).map((l) => l.split(",").map((c) => c.trim().replace(/^"|"$/g, "")))
  .filter((c) => c[0] && !/^student/i.test(c[0]) && /^(6|7|8|9|10|11|12)$/.test(c[1] || ""))
  .map(([student_name, class_grade, parent_phone = ""]) => ({ student_name, class_grade, parent_phone }));

const Choice = ({ value, cur, onPick, title, sub, testid }) => (
  <button type="button" onClick={() => onPick(value)} data-testid={testid}
    className={`text-left p-4 rounded-xl border-2 bg-white transition-[border-color,box-shadow] ${cur === value ? "border-[#FF2E63] shadow-lg" : "border-slate-200 hover:border-slate-400"}`}>
    <div className="font-bold text-[#0A192F]">{title}</div><div className="text-xs text-slate-500 mt-0.5">{sub}</div>
  </button>
);

export const SchoolOnboard = () => {
  const { L } = useLang();
  const [f, setF] = useState(empty);
  const [roster, setRoster] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = (k) => (v) => setF({ ...f, [k]: v });
  const base = FEES[f.area][f.billing];
  const per = f.billing === "yearly" ? L("/year", "/वर्ष") : L("/month", "/माह");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const rows = parseRoster(await file.text());
    setRoster(rows);
    toast[rows.length ? "success" : "error"](rows.length ? L(`${rows.length} students loaded`, `${rows.length} छात्र जोड़े गए`) : L("No valid rows. Use: name, class (6–12), parent phone", "कोई मान्य पंक्ति नहीं। प्रारूप: नाम, कक्षा (6–12), अभिभावक फ़ोन"));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/schools/apply", { ...f, roster });
      setDone(data); setF(empty); setRoster([]);
    } catch (e2) { toast.error(errText(e2.response?.data?.detail, L("Could not submit", "भेजा नहीं जा सका"))); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="school-onboard-done">
      <CheckCircle2 className="w-12 h-12 mx-auto text-green-500" />
      <h3 className="text-xl font-extrabold text-[#0A192F] mt-3">{L("Application received!", "आवेदन मिल गया!")}</h3>
      <p className="text-slate-600 mt-2 text-sm">{L("Our team will review it and email your discounted (20–30% off) centralised bill with a payment link.", "हमारी टीम समीक्षा करके 20–30% छूट वाला केंद्रीकृत बिल और भुगतान लिंक ईमेल करेगी।")}</p>
      <Button variant="outline" className="mt-4 rounded-full" onClick={() => setDone(null)} data-testid="school-onboard-again">{L("Submit another school", "दूसरा स्कूल जोड़ें")}</Button>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="school-onboard-form">
      <div>
        <span className="rs-chip">{L("New school onboarding", "नया स्कूल ऑनबोर्डिंग")}</span>
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A192F] mt-3">{L(...TAGLINE)}</h2>
        <p className="text-slate-600 mt-2 text-sm">{L("20–30% off for new schools till ", "नए स्कूलों के लिए 20–30% छूट, ")}{L(...OFFER_END_LABEL)} · {L("bulk roster · one central bill", "बल्क रोस्टर · एक केंद्रीकृत बिल")}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Choice value="municipal" cur={f.area} onPick={pick("area")} title={L("Municipal area", "नगरपालिका क्षेत्र")} sub={`${fmtINR(150000)}/${L("year", "वर्ष")} · ${fmtINR(20000)}/${L("month", "माह")}`} testid="school-area-municipal" />
        <Choice value="rural" cur={f.area} onPick={pick("area")} title={L("Rural area", "ग्रामीण क्षेत्र")} sub={`${fmtINR(100000)}/${L("year", "वर्ष")} · ${fmtINR(12000)}/${L("month", "माह")}`} testid="school-area-rural" />
        <Choice value="yearly" cur={f.billing} onPick={pick("billing")} title={L("Pay yearly", "वार्षिक भुगतान")} sub={L("Best value", "सबसे किफ़ायती")} testid="school-billing-yearly" />
        <Choice value="monthly" cur={f.billing} onPick={pick("billing")} title={L("Pay monthly", "मासिक भुगतान")} sub={L("Billed every month", "हर महीने बिल")} testid="school-billing-monthly" />
      </div>
      <div className="rounded-2xl bg-[#0A192F] text-white p-5 flex flex-wrap items-end justify-between gap-3" data-testid="school-price-preview">
        <div><div className="text-xs text-slate-300">{L("Base fee", "मूल शुल्क")}</div><div className="text-2xl font-black font-['Outfit']" data-testid="school-base-fee">{fmtINR(base)}{per}</div></div>
        <div className="text-right"><div className="text-xs text-[#FFD166]">{L("After 20–30% onboarding discount", "20–30% ऑनबोर्डिंग छूट के बाद")}</div><div className="text-xl font-black font-['Outfit'] text-[#FFD166]" data-testid="school-discounted-fee">{fmtINR(base * 0.7)} – {fmtINR(base * 0.8)}{per}</div><div className="text-[11px] text-slate-300">+ 18% GST</div></div>
        <p className="w-full text-[11px] text-slate-300">{L(...KITS_NOTE)}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="so_school" label={L("School name", "स्कूल का नाम")} required value={f.school_name} onChange={on("school_name")} />
        <Field id="so_person" label={L("Contact person", "संपर्क व्यक्ति")} required value={f.contact_person} onChange={on("contact_person")} />
        <Field id="so_designation" label={L("Designation", "पद")} value={f.designation} onChange={on("designation")} />
        <Field id="so_email" label={L("Email", "ईमेल")} type="email" required value={f.email} onChange={on("email")} />
        <Field id="so_phone" label={L("Phone (WhatsApp)", "फ़ोन (WhatsApp)")} required value={f.phone} onChange={on("phone")} />
        <Field id="so_city" label={L("City", "शहर")} required value={f.city} onChange={on("city")} />
        <Field id="so_state" label={L("State", "राज्य")} required value={f.state} onChange={on("state")} />
        <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-white px-4 cursor-pointer hover:border-[#FF2E63] h-11 mt-auto" data-testid="school-roster-upload">
          <Upload className="w-4 h-4 text-[#FF2E63]" /><span className="text-sm font-semibold text-[#0A192F] truncate">{roster.length ? L(`${roster.length} students`, `${roster.length} छात्र`) : L("Upload roster (CSV)", "रोस्टर अपलोड (CSV)")}</span>
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="school-roster-input" />
        </label>
      </div>
      <p className="text-[11px] text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{L("CSV columns: student name, class (6–12), parent phone. You can also add the roster later.", "CSV कॉलम: छात्र का नाम, कक्षा (6–12), अभिभावक फ़ोन। रोस्टर बाद में भी जोड़ सकते हैं।")}</p>
      <Button type="submit" disabled={busy} className="w-full rs-cta h-12 rounded-full" data-testid="school-onboard-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Apply for onboarding", "ऑनबोर्डिंग के लिए आवेदन करें")}</Button>
    </form>
  );
};
